import { useEffect, useState } from 'react';
import { ProviderProfileDataOptional } from '../interfaces/profile.interfaces';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useGetFhirProviderbyEmailQuery } from 'graphql/generated/remote-schema-hasura';
import { useAuth } from '../../../auth/context/AuthContext';
import { Roles } from '../../../firebase/interfaces';
import { StateLicenseType } from '../../../graphql/generated/hasura';

export const useGetProviderData = () => {
  const { user } = useAuth();

  const [providerProfileData, setProviderProfileData] =
    useState<ProviderProfileDataOptional | null>(null);
  const [initialProviderProfileData, setInitialProviderProfileData] =
    useState<ProviderProfileDataOptional | null>(null);
  const [updateCount, setUpdateCount] = useState(0);

  const updater = () => setUpdateCount((up) => up + 1);

  const getMfaParts = (mfaPhoneNumber?: string) => {
    try {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        mfaPhoneNumber || '',
      );
      return {
        mfaPhone: parsedPhoneNumber?.nationalNumber,
        mfaCountry: parsedPhoneNumber?.countryCallingCode,
      };
    } catch (error) {
      console.error('Unable to parse phone number');
      return {
        mfaPhone: '',
        mfaCountry: '',
      };
    }
  };

  const isProvider = user?.role === Roles.PROVIDER;

  const { loading, refetch } = useGetFhirProviderbyEmailQuery({
    skip: !isProvider || !user?.email,
    fetchPolicy: 'network-only',
    variables: {
      email: String(user?.email),
    },
    onCompleted: (data) => {
      const providerProfileData = data?.getFHIRProviderbyEmail?.providers[0];

      const education = (providerProfileData?.SENSITIVE_education || []).map(
        (education) => ({
          medicalSchool: education?.medicalSchool,
          residency: education?.residency,
          undergraduate: education?.undergraduate,
          isStillResident: education?.isStillResident,
          residencyEndDate: education?.residencyEndDate,
          residencyStartDate: education?.residencyStartDate,
        }),
      );

      const stateLicenses = (
        providerProfileData?.SENSITIVE_credentials?.stateLicenses || []
      ).map((license) => ({
        licenseNumber: license.licenseNumber,
        state: license.state,
        expirationDate: license.expirationDate,
        licenseType: license.licenseType,
      }));

      const provider = {
        firstname: providerProfileData?.SENSITIVE_firstname || '',
        lastname: providerProfileData?.SENSITIVE_lastname || '',
        email: providerProfileData?.SENSITIVE_email || '',
        gender: providerProfileData?.SENSITIVE_gender || '',
        genderDescribe: providerProfileData?.SENSITIVE_gender_describe || '',
        dateOfBirth: providerProfileData?.SENSITIVE_dob || '',
        phone: providerProfileData?.SENSITIVE_phone?.trim() || '',
        nationalProviderIdentifier:
          providerProfileData?.SENSITIVE_national_provider_identifier || '',
        languagesOfCommunication:
          providerProfileData?.SENSITIVE_languages_of_communication || '',
        credentials: {
          titles: providerProfileData?.SENSITIVE_credentials.titles,
          states: [],
          fellowships:
            providerProfileData?.SENSITIVE_credentials.fellowships?.trim() ??
            undefined,
          certifications:
            providerProfileData?.SENSITIVE_credentials.certifications,
          medicalRole: providerProfileData?.SENSITIVE_credentials.medicalRole,
          yearsOfPractice:
            providerProfileData?.SENSITIVE_credentials.yearsOfPractice,
        },
        about: providerProfileData?.SENSITIVE_about || '',
        education: education || '',
        stateLicenses: stateLicenses.length
          ? stateLicenses
          : [
              {
                expirationDate: '',
                licenseNumber: '',
                state: '',
                licenseType: StateLicenseType.StateLicense,
              },
            ],
        office_information: {
          officeName:
            providerProfileData?.SENSITIVE_office_information?.officeName || '',
          email: providerProfileData?.SENSITIVE_office_information?.email || '',
          phone: providerProfileData?.SENSITIVE_office_information?.phone || '',
          address1:
            providerProfileData?.SENSITIVE_office_information?.address1 || '',
          address2:
            providerProfileData?.SENSITIVE_office_information?.address2 || '',
          country:
            providerProfileData?.SENSITIVE_office_information?.country || '',
          state: providerProfileData?.SENSITIVE_office_information?.state || '',
          city: providerProfileData?.SENSITIVE_office_information?.city || '',
          zipCode:
            providerProfileData?.SENSITIVE_office_information?.zipCode || '',
        },
        address_information: {
          address1:
            providerProfileData?.SENSITIVE_address_information?.address1 || '',
          address2:
            providerProfileData?.SENSITIVE_address_information?.address2 || '',
          country:
            providerProfileData?.SENSITIVE_address_information?.country || '',
          state:
            providerProfileData?.SENSITIVE_address_information?.state || '',
          city: providerProfileData?.SENSITIVE_address_information?.city || '',
          zipCode:
            providerProfileData?.SENSITIVE_address_information?.zipCode || '',
        },
        hide_contact_info: providerProfileData?.hide_contact_info || false,
        hide_office_address: providerProfileData?.hide_office_address || false,
        providerBalance: providerProfileData?.providerBalance || null,
        signInProvider: user?.signInProvider || null,
        ...getMfaParts(user?.mfaPhoneNumber),
        acuityCalendarId: providerProfileData?.acuityCalendarId || '',
        fullscriptId: providerProfileData?.fullscriptId || '',
        ePrescribeUserId: providerProfileData?.ePrescribeUserId || '',
        stripePayoutsReadyAt: providerProfileData?.stripePayoutsReadyAt ?? '',
        isStripeConnected: providerProfileData?.isStripeConnected ?? false,
      };
      setProviderProfileData(provider);
      setInitialProviderProfileData(provider);
    },
  });

  useEffect(() => {
    if (!isProvider) return;
    refetch();
  }, [refetch, updateCount, isProvider]);

  return {
    fhirProviderDataLoading: loading,
    providerProfileData,
    initialProviderProfileData,
    setProviderProfileData,
    updater,
  };
};
